// pages/blog/adu-mother-in-law-suite.js

import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';

export const metadata = {
  title: 'Converting Your ADU Into the Perfect Mother-in-Law Suite',
  image: '/blog/adu-mother-in-law-suite.webp',
  date: 'June 23, 2025',
  author: 'Modern Build Team',
  excerpt:
    'Learn how to convert your garage into a stylish and functional ADU mother-in-law suite. Design tips, permits, layout ideas, and how to add value to your home.',
  category: 'ADU Remodeling',
  tags: ['ADU', 'Garage Conversion', 'Mother-in-Law Suite', 'Home Renovation'],
};

export default function ADUMotherInLawSuite() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/blog/adu-mother-in-law-suite`} />

        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/adu-mother-in-law-suite`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "ADU Garage And Mother In Law Suite",
                  "item": `https://modernbuildremodel.com/blog/adu-mother-in-law-suite`
                },
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'adu-mother-in-law-suite')),
          }}
        />
      </Head>

      <Header />

      <div className="bg-white">
        <Hero category="aduGarageMotherInLawSuite" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
            <span className="capitalize text-primary tracking-[3px]">By {metadata.author} / {metadata.date}</span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`Modern Build Home Remodeling post: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

      <div className="prose prose-lg max-w-none text-md text-black leading-relaxed space-y-6">
        <p><strong>Accessory Dwelling Units (ADUs)</strong> have become one of the most popular and versatile home upgrades across California. Whether you're planning to house aging parents, welcome extended family, or even generate passive income, converting your garage into a <strong>mother-in-law suite</strong> is a smart, future-ready investment.</p>

        <p>Unlike new construction, garage conversions can often be completed faster and at a lower cost—especially when the existing structure is sound. But creating a legal, safe, and truly livable ADU requires more than just flooring and drywall. It demands smart design, code compliance, and thoughtful planning tailored to how the space will be used.</p>

        <h3 className="font-bold">✅ Understand Local ADU Laws & Permits</h3>
        <p>Before breaking ground, it's critical to research your city and county zoning codes. Many municipalities now allow garage-to-ADU conversions under relaxed state housing laws, but you'll still need to meet requirements for:</p>
        <ul className="list-disc pl-6">
          <li>Minimum ceiling height</li>
          <li>Fire and egress standards</li>
          <li>Setbacks and lot coverage</li>
          <li>Utility access and sewer connections</li>
        </ul>
        <p><strong>Modern Build</strong> simplifies this process by handling all permits, inspections, and coordination with local building departments—so you don’t have to.</p>

        <h3 className="font-bold">🏡 Designing for Comfort and Independence</h3>
        <p>A great ADU feels like a real home, not a converted garage. That means including:</p>
        <ul className="list-disc pl-6">
          <li>A private entrance for independence</li>
          <li>Full bathroom with modern fixtures</li>
          <li>Efficient kitchenette or full kitchen</li>
          <li>Bedroom and multipurpose living space</li>
        </ul>
        <p>Even within tight square footage, good layout choices and natural light can create a warm, inviting environment.</p>

        <h3 className="font-bold">🔧 Insulation, Plumbing & Electrical</h3>
        <p>Garages aren't meant for living—so transforming one into a habitable unit means upgrading the infrastructure. This includes:</p>
        <ul className="list-disc pl-6">
          <li>Insulation for all walls, ceilings, and floors</li>
          <li>Dedicated HVAC or mini-split system</li>
          <li>Safe, upgraded electrical paneling</li>
          <li>Running plumbing to kitchen and bath</li>
        </ul>
        <p>Our team ensures every component meets California residential code—and functions like a full apartment.</p>

        <h3 className="font-bold">♿ Accessibility & Aging in Place</h3>
        <p>If you're designing for aging parents or future-proofing your home, <strong>universal design</strong> is key. Features like no-step entries, wide doorways, lever-style handles, and roll-in showers can make the space usable for decades to come.</p>

        <h3 className="font-bold">📐 Maximizing Space with Smart Design</h3>
        <p>Garage ADUs often require creative planning to feel spacious. We use:</p>
        <ul className="list-disc pl-6">
          <li>Custom built-ins and wall beds</li>
          <li>Multifunctional furniture</li>
          <li>Mirrors and light-colored materials</li>
          <li>Sliding or pocket doors</li>
        </ul>
        <p>These details help your compact unit feel larger, more open, and far more livable.</p>

        <h3 className="font-bold">📈 Boosting Home Value & Future Options</h3>
        <p>Beyond comfort and flexibility, a well-executed ADU increases your property’s market value and income potential. Whether you use it as:</p>
        <ul className="list-disc pl-6">
          <li>A rental property</li>
          <li>A guest suite for visiting family</li>
          <li>A home office or creative studio</li>
          <li>A live-in caregiver’s quarters</li>
        </ul>
        <p>—an ADU offers lasting ROI and functionality for years to come.</p>

        <p className="mt-8"><strong>Whether you're building for family or future income</strong>, a garage conversion into a modern ADU is one of the most impactful upgrades you can make. At <strong>Modern Build</strong>, we handle everything—from permits and floorplans to finishes and final inspections—so you get a fully turnkey experience with zero stress.</p>
      </div>

        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
